import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Chart from 'react-apexcharts';
import Loader from '../../component/common/Loader';
import { minsToHhMm } from '../SleepHub/sleepHubApi';
import { fetchBreathworkUserDetail, secsToMmSs } from './breathworkHubApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  PANEL_ROW_CLASS,
  Chip,
  EntityHeader,
  BackLink,
  STAT_ICONS,
  CHART_BASE,
  CHART_GRID,
  CHART_GRID_NO_X,
  toneChip,
  paletteAt
} from '../../component/common/designSystem';
import {
  SESSION_TYPE_COLORS,
  experienceChipClass,
  sessionTypeChipClass,
  sessionTypeLabel
} from './components/breathworkTheme';

const dayLabel = (date) =>
  date ? new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '-';

/**
 * Breathwork → User profile.
 * One user's breathwork life: session stats and their hold/exhale history,
 * streaks, personal bests, profile (goals + experience) and their breathwork
 * audio listening history.
 * Reused by the company panel via fetcher/backPath/backLabel props.
 */
const BreathworkUserDetail = ({
  fetcher = fetchBreathworkUserDetail,
  backPath: backPathProp,
  backLabel: backLabelProp
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  // Present only when arriving from a company page (B2B flow).
  const companyId = searchParams.get('companyId');

  const backPath =
    backPathProp || (companyId ? `/breathwork/b2b/company/${companyId}` : '/breathwork/b2c');
  const backLabel = backLabelProp || (companyId ? 'Back to Company' : 'Back to B2C Logs');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      navigate(backPath, { replace: true });
      return;
    }
    let mounted = true;
    setLoader(true);
    fetcher(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetcher]);

  const user = detail?.user;
  const sessions = detail?.breathworkSessions || [];
  const topBreathworks = detail?.topBreathworks || [];
  const history = detail?.breathworkHistory || [];

  /**
   * Each session as its own bar, coloured by technique. Distributed colouring
   * rather than two series: the sessions are a single timeline, and splitting
   * them into a Hold series and an Exhale series would leave a gap everywhere
   * the user practised the other one.
   */
  const sessionOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: sessions.map((session) =>
      session.sessionType === 'BREATH_EXHALE' ? SESSION_TYPE_COLORS[1] : SESSION_TYPE_COLORS[0]
    ),
    plotOptions: { bar: { borderRadius: 4, columnWidth: '55%', distributed: true } },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: CHART_GRID,
    xaxis: { categories: sessions.map((session) => dayLabel(session.date)) },
    yaxis: { labels: { formatter: (val) => secsToMmSs(val) } },
    tooltip: {
      // Two sessions can share a date, so the bar has to name its own
      // technique — the x-axis label alone can't tell them apart.
      y: {
        formatter: (val, opts) =>
          `${secsToMmSs(val)} — ${
            sessionTypeLabel(sessions[opts?.dataPointIndex]?.sessionType) || 'Session'
          }`
      }
    }
  };

  const topOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: topBreathworks.map((entry, index) => paletteAt(index)),
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: '60%', distributed: true }
    },
    dataLabels: { enabled: true, style: { fontSize: '12px' } },
    legend: { show: false },
    grid: CHART_GRID_NO_X,
    xaxis: { categories: topBreathworks.map((entry) => entry.breathwork) },
    tooltip: { y: { formatter: (val) => `${val} plays` } }
  };

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>User Breathwork Profile | Shoorah Admin</title>
      </Helmet>
      {loader ? <Loader /> : null}

      <BackLink label={backLabel} onClick={() => navigate(backPath)} />

      {user && (
        <EntityHeader
          className="mt-4"
          image={user.user_profile}
          name={user.name}
          subtitle={user.email}
          chips={
            user.experience || (user.goals || []).length > 0 ? (
              <>
                {user.experience && (
                  <Chip className={experienceChipClass(user.experience)}>{user.experience}</Chip>
                )}
                {(user.goals || []).map((goal) => (
                  <Chip key={goal}>{goal}</Chip>
                ))}
              </>
            ) : null
          }
          action={
            <Chip className={toneChip('brand')}>
              {user.accountType === 'B2B' ? `B2B — ${user.company}` : 'B2C'}
            </Chip>
          }
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 mt-6">
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Total Sessions"
          value={user ? user.totalSessions : '-'}
        />
        <StatCard
          icon={STAT_ICONS.time}
          title="Session Time"
          value={user ? minsToHhMm(user.totalSessionTimeMins) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.streak}
          title="Streak"
          value={user ? `${user.currentStreak} days` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.personalBest}
          title="Max Hold"
          value={user ? secsToMmSs(user.maxHold) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.personalBest}
          title="Max Exhale"
          value={user ? secsToMmSs(user.maxExhale) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.audioLibrary}
          title="Top Audio"
          value={detail?.mostPlayedBreathwork || '-'}
        />
      </div>

      {/* ---------- Charts ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Session Lengths" subtitle="Last 14 sessions — Breath Hold vs Breath Exhale">
          {sessions.length > 0 ? (
            <Chart
              type="bar"
              height={320}
              width="100%"
              series={[
                { name: 'Duration', data: sessions.map((session) => session.durationSeconds) }
              ]}
              options={sessionOptions}
            />
          ) : (
            <PanelEmpty>No sessions recorded yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Top Breathwork Audios" subtitle="This user's most played">
          {topBreathworks.length > 0 ? (
            <Chart
              type="bar"
              height={320}
              width="100%"
              series={[{ name: 'Plays', data: topBreathworks.map((entry) => entry.plays) }]}
              options={topOptions}
            />
          ) : (
            <PanelEmpty>No audio activity yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- History ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Recent Sessions" subtitle="Most recent breathwork sessions">
          {sessions.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium">Session</th>
                  <th className="py-2 font-medium text-right">Duration</th>
                </>
              }
            >
              {[...sessions].reverse().map((session, index) => (
                <tr key={`${session.date}-${index}`} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{session.date}</td>
                  <td className="py-2 pr-4">
                    {sessionTypeLabel(session.sessionType) ? (
                      <Chip className={sessionTypeChipClass(session.sessionType)}>
                        {sessionTypeLabel(session.sessionType)}
                      </Chip>
                    ) : (
                      session.breathwork || '-'
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {session.durationSeconds != null
                      ? secsToMmSs(session.durationSeconds)
                      : session.duration || '-'}
                  </td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>This user has not completed a breathwork session yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Audio Listening History" subtitle="Most recent guided sessions">
          {history.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium">Audio</th>
                  <th className="py-2 pr-4 font-medium text-right">Plays</th>
                  <th className="py-2 font-medium text-right">Listened</th>
                </>
              }
            >
              {history.map((entry, index) => (
                <tr key={`${entry.date}-${index}`} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{entry.date}</td>
                  <td className="py-2 pr-4">{entry.breathwork}</td>
                  <td className="py-2 pr-4 text-right">{entry.plays}</td>
                  <td className="py-2 text-right">{minsToHhMm(entry.listenMins)}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No listening history yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

BreathworkUserDetail.propTypes = {
  fetcher: PropTypes.func,
  backPath: PropTypes.string,
  backLabel: PropTypes.string
};

export default BreathworkUserDetail;
